import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Spinner, Alert, Card, Row, Col } from 'react-bootstrap';
import ReactQuill from 'react-quill';

import VereinbarungService from '../services/vereinbarung.service';
import UserService from '../services/user.service';
import PlatzhalterAuswahlModal from './PlatzhalterAuswahlModal';

const VereinbarungBearbeiten = () => {
    const { id } = useParams(); // Vereinbarungs-ID aus der URL
    const navigate = useNavigate();
    
    // === ZUSTANDSVARIABLEN ===
    const [titelId, setTitelId] = useState('');
    const [partnerId, setPartnerId] = useState('');
    const [inhalt, setInhalt] = useState('');
    const [bearbeiterVon, setBearbeiterVon] = useState('');
    const [bearbeiterAn, setBearbeiterAn] = useState('');
    const [zusammenfassung, setZusammenfassung] = useState('');
    const [anlagen, setAnlagen] = useState('');
    const [gueltigkeitVon, setGueltigkeitVon] = useState('');
    const [gueltigkeitBis, setGueltigkeitBis] = useState('');
    const [kuendigungsfristWert, setKuendigungsfristWert] = useState('');
    const [kuendigungsfristEinheit, setKuendigungsfristEinheit] = useState('Tag(e)');
    const [status, setStatus] = useState('');

    const [titelList, setTitelList] = useState([]);
    const [partnerList, setPartnerList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [modalShow, setModalShow] = useState(false);
    const quillRef = useRef(null);

    useEffect(() => {
        loadFormData();
    }, [id]);

    const loadFormData = async () => {
        setLoading(true);
        try {
            // Lade die vorhandene Vereinbarung und die Formulardaten parallel
            const [vereinbarungRes, titelRes, partnerRes] = await Promise.all([
                VereinbarungService.getAgreementById(id),
                VereinbarungService.getAllAgreementTitles(),
                UserService.getAllAds({ page: 1, search: '', sort: 'company_name', order: 'asc' })
            ]);

            const vereinbarung = vereinbarungRes.data;
            
            // Setze alle Formularfelder mit den vorhandenen Daten
            setTitelId(vereinbarung.titel_id || '');
            setPartnerId(vereinbarung.empfaenger_user_id || '');
            setInhalt(vereinbarung.inhalt || '');
            setBearbeiterVon(vereinbarung.bearbeiter_von || '');
            setBearbeiterAn(vereinbarung.bearbeiter_an || '');
            setZusammenfassung(vereinbarung.kurze_zusammenfassung || '');
            setAnlagen(vereinbarung.anlagen || '');
            setStatus(vereinbarung.status || 'Entwurf');
            
            // Datumsfelder formatieren für Input-Felder
            if (vereinbarung.gueltigkeit_von) {
                const dateVon = new Date(vereinbarung.gueltigkeit_von);
                setGueltigkeitVon(dateVon.toISOString().split('T')[0]);
            }
            if (vereinbarung.gueltigkeit_bis) {
                const dateBis = new Date(vereinbarung.gueltigkeit_bis);
                setGueltigkeitBis(dateBis.toISOString().split('T')[0]);
            }
            
            setKuendigungsfristWert(vereinbarung.kuendigungsfrist_wert || '');
            setKuendigungsfristEinheit(vereinbarung.kuendigungsfrist_einheit || 'Tag(e)');
            
            // Setze die Listen für die Dropdowns
            setTitelList(titelRes.data);
            setPartnerList(partnerRes.data.ads);
            
            setLoading(false);
        } catch (err) {
            setError("Fehler beim Laden der Vereinbarung: " + (err.response?.data?.message || err.message));
            setLoading(false);
        }
    };
    
    const handleSelectPlaceholder = (placeholderKey) => {
        const editor = quillRef.current.getEditor();
        const range = editor.getSelection(true);
        editor.insertText(range.index, placeholderKey, 'user');
    };

    const handleUpdate = (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!titelId || !partnerId || !inhalt) {
            setError("Bitte füllen Sie die Pflichtfelder (Titel, Partner, Inhalt) aus.");
            return;
        }

        setSubmitting(true);
        
        // Datenobjekt für die Aktualisierung
        const updateData = {
            titel_id: titelId,
            empfaenger_user_id: partnerId,
            inhalt: inhalt,
            bearbeiter_von: bearbeiterVon,
            bearbeiter_an: bearbeiterAn,
            kurze_zusammenfassung: zusammenfassung,
            anlagen: anlagen,
            gueltigkeit_von: gueltigkeitVon || null,
            gueltigkeit_bis: gueltigkeitBis || null,
            kuendigungsfrist_wert: kuendigungsfristWert || null,
            kuendigungsfrist_einheit: kuendigungsfristWert ? kuendigungsfristEinheit : null,
            status: status
        };

        // Verwende die update Methode statt create
        VereinbarungService.updateAgreement(id, updateData)
            .then(() => {
                setSuccess("Vereinbarung erfolgreich aktualisiert! Sie werden weitergeleitet...");
                setTimeout(() => {
                    navigate(`/agreements/${id}`);
                }, 2000);
            })
            .catch(err => {
                const errorMessage = (err.response?.data?.message) || "Ein unbekannter Fehler ist aufgetreten.";
                setError(errorMessage);
                setSubmitting(false);
            });
    };

    // Moderne Styles (gleich wie in VereinbarungErstellen)
    const styles = {
        pageContainer: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            minHeight: '100vh',
            paddingTop: '40px',
            paddingBottom: '60px'
        },
        mainContainer: {
            maxWidth: '1200px',
            margin: '0 auto'
        },
        header: {
            color: '#ffffff',
            fontSize: '42px',
            fontWeight: '700',
            marginBottom: '40px',
            textAlign: 'center',
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            letterSpacing: '-0.5px'
        },
        mainCard: {
            borderRadius: '25px',
            border: 'none',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            background: '#ffffff'
        },
        cardHeader: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '25px 35px',
            borderBottom: 'none'
        },
        cardBody: {
            padding: '40px 35px',
            background: '#ffffff'
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: '600',
            color: '#667eea',
            marginBottom: '25px',
            marginTop: '35px',
            paddingBottom: '10px',
            borderBottom: '2px solid #f0f2ff',
            display: 'flex',
            alignItems: 'center'
        },
        sectionIcon: {
            marginRight: '10px',
            fontSize: '20px'
        },
        formGroup: {
            marginBottom: '25px'
        },
        formLabel: {
            fontWeight: '500',
            color: '#4a5568',
            marginBottom: '8px',
            fontSize: '14px',
            letterSpacing: '0.3px'
        },
        requiredStar: {
            color: '#e53e3e',
            marginLeft: '4px'
        },
        formControl: {
            borderRadius: '12px',
            border: '2px solid #e2e8f0',
            padding: '12px 16px',
            fontSize: '15px',
            transition: 'all 0.3s ease',
            backgroundColor: '#f7fafc'
        },
        formSelect: {
            borderRadius: '12px',
            border: '2px solid #e2e8f0',
            padding: '12px 16px',
            fontSize: '15px',
            transition: 'all 0.3s ease',
            backgroundColor: '#f7fafc',
            cursor: 'pointer'
        },
        textArea: {
            borderRadius: '12px',
            border: '2px solid #e2e8f0',
            padding: '12px 16px',
            fontSize: '15px',
            transition: 'all 0.3s ease',
            backgroundColor: '#f7fafc',
            resize: 'vertical',
            minHeight: '100px'
        },
        placeholderButton: {
            background: 'linear-gradient(135deg, #ffb347 0%, #ffcc33 100%)',
            border: 'none',
            borderRadius: '10px',
            padding: '8px 20px',
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '15px',
            boxShadow: '0 4px 15px rgba(255, 179, 71, 0.3)',
            transition: 'all 0.3s ease'
        },
        quillContainer: {
            borderRadius: '12px',
            overflow: 'hidden',
            border: '2px solid #e2e8f0',
            backgroundColor: '#f7fafc'
        },
        submitButton: {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '12px',
            padding: '14px 40px',
            fontSize: '16px',
            fontWeight: '600',
            letterSpacing: '0.5px',
            boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
            transition: 'all 0.3s ease',
            marginTop: '30px'
        },
        cancelButton: {
            background: 'linear-gradient(135deg, #636e72 0%, #2d3436 100%)',
            border: 'none',
            borderRadius: '12px',
            padding: '14px 40px',
            fontSize: '16px',
            fontWeight: '600',
            letterSpacing: '0.5px',
            boxShadow: '0 10px 30px rgba(45, 52, 54, 0.3)',
            transition: 'all 0.3s ease',
            marginTop: '30px',
            marginRight: '15px'
        },
        alert: {
            borderRadius: '12px',
            padding: '16px 20px',
            marginTop: '20px',
            marginBottom: '20px',
            border: 'none',
            fontSize: '15px'
        },
        successAlert: {
            background: 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)',
            color: '#0c5460'
        },
        errorAlert: {
            background: 'linear-gradient(135deg, #fccb90 0%, #ff9a9e 100%)',
            color: '#721c24'
        },
        loadingContainer: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        },
        loadingSpinner: {
            width: '60px',
            height: '60px',
            borderWidth: '4px',
            borderColor: '#ffffff',
            borderTopColor: 'transparent'
        },
        loadingText: {
            color: '#ffffff',
            fontSize: '18px',
            marginTop: '20px',
            fontWeight: '500'
        },
        infoBox: {
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            borderRadius: '15px',
            padding: '20px',
            marginBottom: '30px',
            color: '#ffffff'
        },
        infoText: {
            margin: '0',
            fontSize: '14px',
            lineHeight: '1.6'
        },
        statusBadge: {
            display: 'inline-block',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: '600',
            marginLeft: '10px'
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <Spinner animation="border" style={styles.loadingSpinner} />
                <p style={styles.loadingText}>Lade Vereinbarung...</p>
            </div>
        );
    }

    return (
        <div style={styles.pageContainer}>
            <Container style={styles.mainContainer}>
                <h1 style={styles.header}>📝 Vereinbarung bearbeiten</h1>
                
                <Card style={styles.mainCard}>
                    <div style={styles.cardHeader}>
                        <div style={styles.infoBox}>
                            <p style={styles.infoText}>
                                ✏️ Sie bearbeiten eine bestehende Vereinbarung. Die Änderungen werden direkt gespeichert.
                                Wenn Sie eine neue Version erstellen möchten, nutzen Sie stattdessen den Button "Neue Version erstellen".
                            </p>
                        </div>
                    </div>
                    
                    <Card.Body style={styles.cardBody}>
                        <Form onSubmit={handleUpdate}>
                            {/* Hauptinformationen */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📝</span>
                                Hauptinformationen
                                <span style={{
                                    ...styles.statusBadge,
                                    background: status === 'Entwurf' ? '#95a5a6' : 
                                               status === 'Unterzeichnet' ? '#27ae60' : '#f39c12',
                                    color: '#ffffff'
                                }}>
                                    Status: {status}
                                </span>
                            </div>
                            
                            <Row>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formTitel">
                                        <Form.Label style={styles.formLabel}>
                                            Vereinbarungstitel
                                            <span style={styles.requiredStar}>*</span>
                                        </Form.Label>
                                        <Form.Select 
                                            style={styles.formSelect}
                                            value={titelId} 
                                            onChange={e => setTitelId(e.target.value)} 
                                            required
                                        >
                                            <option value="">Bitte Titel auswählen</option>
                                            {titelList.map(titel => (
                                                <option key={titel.titel_id} value={titel.titel_id}>{titel.titel}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formPartner">
                                        <Form.Label style={styles.formLabel}>
                                            Vertragspartner
                                            <span style={styles.requiredStar}>*</span>
                                        </Form.Label>
                                        <Form.Select 
                                            style={styles.formSelect}
                                            value={partnerId} 
                                            onChange={e => setPartnerId(e.target.value)} 
                                            required
                                        >
                                            <option value="">Bitte Partner auswählen</option>
                                            {partnerList.map(partner => (
                                                partner.user_account && (
                                                    <option key={partner.adm_id} value={partner.user_account.id}>
                                                        {partner.company_name}
                                                    </option>
                                                )
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Status */}
                            <Row>
                                <Col md={12}>
                                    <Form.Group style={styles.formGroup} controlId="formStatus">
                                        <Form.Label style={styles.formLabel}>Status der Vereinbarung</Form.Label>
                                        <Form.Select 
                                            style={styles.formSelect}
                                            value={status} 
                                            onChange={e => setStatus(e.target.value)}
                                        >
                                            <option value="Entwurf">Entwurf</option>
                                            <option value="Unterzeichnet">Unterzeichnet</option>
                                            <option value="Archiviert">Archiviert</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* Bearbeiter */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>👥</span>
                                Bearbeiter
                            </div>
                            
                            <Row>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formBearbeiterVon">
                                        <Form.Label style={styles.formLabel}>Bearbeiter (Von)</Form.Label>
                                        <Form.Control 
                                            style={styles.formControl}
                                            type="text" 
                                            value={bearbeiterVon} 
                                            onChange={e => setBearbeiterVon(e.target.value)} 
                                            placeholder="Name eingeben..."
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formBearbeiterAn">
                                        <Form.Label style={styles.formLabel}>Bearbeiter (An)</Form.Label>
                                        <Form.Control 
                                            style={styles.formControl}
                                            type="text" 
                                            value={bearbeiterAn} 
                                            onChange={e => setBearbeiterAn(e.target.value)} 
                                            placeholder="Name eingeben..."
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            {/* Zeitraum */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📅</span>
                                Gültigkeitszeitraum
                            </div>
                            
                            <Row>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formGueltigkeitVon">
                                        <Form.Label style={styles.formLabel}>Gültig von</Form.Label>
                                        <Form.Control 
                                            style={styles.formControl}
                                            type="date" 
                                            value={gueltigkeitVon} 
                                            onChange={e => setGueltigkeitVon(e.target.value)} 
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group style={styles.formGroup} controlId="formGueltigkeitBis">
                                        <Form.Label style={styles.formLabel}>Gültig bis</Form.Label>
                                        <Form.Control 
                                            style={styles.formControl}
                                            type="date" 
                                            value={gueltigkeitBis} 
                                            onChange={e => setGueltigkeitBis(e.target.value)} 
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            {/* Inhalt */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📄</span>
                                Vereinbarungsinhalt
                            </div>
                            
                            <Form.Group style={styles.formGroup} controlId="formZusammenfassung">
                                <Form.Label style={styles.formLabel}>Kurze Zusammenfassung</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={2} 
                                    style={styles.textArea}
                                    value={zusammenfassung} 
                                    onChange={e => setZusammenfassung(e.target.value)} 
                                    placeholder="Geben Sie eine kurze Zusammenfassung ein..."
                                />
                            </Form.Group>

                            <Form.Group style={styles.formGroup} controlId="formInhalt">
                                <Form.Label style={styles.formLabel}>
                                    Inhalt der Vereinbarung
                                    <span style={styles.requiredStar}>*</span>
                                </Form.Label>
                                <Button 
                                    variant="warning"
                                    size="sm" 
                                    style={styles.placeholderButton}
                                    onClick={() => setModalShow(true)}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 6px 20px rgba(255, 179, 71, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 4px 15px rgba(255, 179, 71, 0.3)';
                                    }}
                                >
                                    🏷️ Platzhalter einfügen
                                </Button>
                                <div style={styles.quillContainer}>
                                    <ReactQuill 
                                        ref={quillRef} 
                                        theme="snow" 
                                        value={inhalt} 
                                        onChange={setInhalt} 
                                        className="resizable-editor" 
                                        style={{ minHeight: '300px' }} 
                                    />
                                </div>
                            </Form.Group>

                            {/* Zusätzliche Informationen */}
                            <div style={styles.sectionTitle}>
                                <span style={styles.sectionIcon}>📎</span>
                                Zusätzliche Informationen
                            </div>

                            <Form.Group style={styles.formGroup} controlId="formAnlagen">
                                <Form.Label style={styles.formLabel}>Anlagen</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3} 
                                    style={styles.textArea}
                                    value={anlagen} 
                                    onChange={e => setAnlagen(e.target.value)} 
                                    placeholder="Eine Anlage pro Zeile..."
                                />
                            </Form.Group>

                            <Form.Group as={Row} style={styles.formGroup} controlId="formKuendigungsfrist">
                                <Form.Label column sm={2} style={styles.formLabel}>
                                    ⏰ Kündigungsfrist
                                </Form.Label>
                                <Col sm={5}>
                                    <Form.Control 
                                        style={styles.formControl}
                                        type="number" 
                                        value={kuendigungsfristWert} 
                                        onChange={e => setKuendigungsfristWert(e.target.value)} 
                                        placeholder="z.B. 3"
                                    />
                                </Col>
                                <Col sm={5}>
                                    <Form.Select 
                                        style={styles.formSelect}
                                        value={kuendigungsfristEinheit} 
                                        onChange={e => setKuendigungsfristEinheit(e.target.value)}
                                    >
                                        <option>Tag(e)</option>
                                        <option>Woche(n)</option>
                                        <option>Monat(e)</option>
                                        <option>Jahre</option>
                                    </Form.Select>
                                </Col>
                            </Form.Group>

                            {error && (
                                <Alert style={{...styles.alert, ...styles.errorAlert}}>
                                    ⚠️ {error}
                                </Alert>
                            )}
                            {success && (
                                <Alert style={{...styles.alert, ...styles.successAlert}}>
                                    ✅ {success}
                                </Alert>
                            )}

                            <div style={{ textAlign: 'center' }}>
                                <Button 
                                    variant="secondary" 
                                    onClick={() => navigate(`/agreements/${id}`)}
                                    style={styles.cancelButton}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 15px 40px rgba(45, 52, 54, 0.4)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 10px 30px rgba(45, 52, 54, 0.3)';
                                    }}
                                >
                                    🚫 Abbrechen
                                </Button>
                                <Button 
                                    variant="primary" 
                                    type="submit" 
                                    disabled={submitting}
                                    style={styles.submitButton}
                                    onMouseEnter={(e) => {
                                        if (!submitting) {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 15px 40px rgba(102, 126, 234, 0.4)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.3)';
                                    }}
                                >
                                    {submitting ? (
                                        <>
                                            <Spinner 
                                                as="span" 
                                                animation="border" 
                                                size="sm" 
                                                role="status" 
                                                aria-hidden="true" 
                                                style={{ marginRight: '10px' }}
                                            />
                                            Speichert...
                                        </>
                                    ) : (
                                        <>💾 Änderungen speichern</>
                                    )}
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>

            <PlatzhalterAuswahlModal 
                show={modalShow}
                onHide={() => setModalShow(false)}
                onSelectPlaceholder={handleSelectPlaceholder}
            />
        </div>
    );
};

export default VereinbarungBearbeiten;